"use client";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Form, FormField, FormInput, Popup, Snackbar } from "@akashnetwork/ui/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { useSnackbar } from "notistack";
import { z } from "zod";

import { useServices } from "@src/context/ServicesProvider";
import { settingsIdAtom } from "@src/context/SettingsProvider/settingsStore";

const formSchema = z.object({
  name: z.string()
});

type Props = {
  dseq: string | number | null | undefined;
  onClose: () => void;
  onSaved: () => void;
  getDeploymentName: (dseq: string | number | null) => string | null;
};

export const DeploymentNameModal: React.FC<Props> = ({ dseq, onClose, onSaved, getDeploymentName }) => {
  const { deploymentLocalStorage } = useServices();
  const [address] = useAtom(settingsIdAtom);
  const formRef = useRef<HTMLFormElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      name: ""
    },
    resolver: zodResolver(formSchema)
  });
  const { handleSubmit, control, setValue } = form;

  useEffect(() => {
    if (dseq) {
      const name = getDeploymentName(dseq);
      setValue("name", name || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dseq, getDeploymentName]);

  const onSaveClick = (event: React.MouseEvent) => {
    event.preventDefault();
    formRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  };

  function onSubmit({ name }: z.infer<typeof formSchema>) {
    deploymentLocalStorage.update(address, dseq, { name: name });

    enqueueSnackbar(
      <Snackbar title={intl.formatMessage({ id: "localnote.deploymentnamemodal.success", defaultMessage: "Success!" })} iconVariant="success" />,
      {
        variant: "success",
        autoHideDuration: 1000
      }
    );

    onSaved();
  }

  return (
    <Popup
      fullWidth
      open={!!dseq}
      variant="custom"
      title={intl.formatMessage(
        { id: "localnote.deploymentnamemodal.title", defaultMessage: "Change Deployment Name {suffix}" },
        { suffix: dseq ? `(${dseq})` : "" }
      )}
      actions={[
        {
          label: intl.formatMessage({ id: "localnote.deploymentnamemodal.close", defaultMessage: "Close" }),
          color: "secondary",
          variant: "ghost",
          side: "left",
          onClick: onClose
        },
        {
          label: intl.formatMessage({ id: "localnote.deploymentnamemodal.save", defaultMessage: "Save" }),
          color: "primary",
          variant: "default",
          side: "right",
          onClick: onSaveClick
        }
      ]}
      onClose={onClose}
      maxWidth="xs"
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
          <FormField
            control={control}
            name="name"
            render={({ field }) => {
              return (
                <FormInput {...field} label={intl.formatMessage({ id: "localnote.deploymentnamemodal.name", defaultMessage: "Name" })} autoFocus type="text" />
              );
            }}
          />
        </form>
      </Form>
    </Popup>
  );
};
